/**
 * ============================================================================
 * MIGRATION v1: Multi-Product Structure
 * ============================================================================
 *
 * Added: October 2025
 *
 * Purpose:
 * Migrates user data from single flat product structure to multi-product
 * array structure. This enables users to select and compare multiple acoustic
 * products within a single room configuration.
 *
 * Changes:
 * 1. Product Data: Flat fields → products[] array with UUIDs
 * 2. Room Data: Flat fields → nested room{} object
 * 3. Product Catalog: Refreshes stale product data from current catalog
 *
 * Impact:
 * - All existing users with saved progress will be migrated automatically
 * - Users can continue exactly where they left off
 * - No data loss - all user selections and progress preserved
 *
 * ============================================================================
 */

import { generateUUID } from '@/lib/uuid.js';

/**
 * Checks if user data needs v1 migration
 *
 * @param {Object} userData - User data from localStorage
 * @returns {boolean} - True if migration is needed
 */
function needsMigration(userData) {
    // Check for old structure indicators:
    // 1. Has old 'product' field with a value (string, not array)
    // 2. Does NOT have new 'products' array yet
    // 3. Has room data (indicates user has progressed through app)

    const hasOldProductData = typeof userData.product === 'string' && userData.product.length > 0;
    const hasNewProductData = Array.isArray(userData.products) && userData.products.length > 0;
    const hasRoomData = typeof userData.roomwidth !== 'undefined';

    return hasOldProductData && !hasNewProductData && hasRoomData;
}

/**
 * Main migration function for v1
 * Transforms flat product/room structure into nested multi-product structure
 *
 * @param {Object} userData - User data from localStorage
 * @returns {Object} - Migrated user data
 */
function migrate(userData) {
    console.log('[Migration v1] Starting: Flat product → Multi-product array');

    // Generate unique ID for the product (using centralized UUID generator with fallback)
    const productId = generateUUID();

    // ========================================================================
    // PRODUCT MIGRATION
    // ========================================================================
    // Transform flat product fields into structured product object
    // Old: { product: "KU", productsize: "Medium", productdata: {...}, ... }
    // New: { products: [{ id: "uuid", product: "KU", size: "Medium", ... }] }

    const migratedProduct = {
        id: productId, // New: UUID for tracking
        product: userData.product || '', // Product code (e.g., "KU")
        size: userData.productsize || '', // Size selection (e.g., "Medium")
        size_variant: userData.productsizevariant || '', // Variant if applicable
        metric_sabins: userData.productsize_metric_sabins || '', // Absorption coefficient (required for calculations)
        data: userData.productdata || null, // Product catalog data (will be refreshed)
        dataExtended: [], // Extended data array (for lumen options)
        calculation: null, // Calculation results (will be recalculated)
        manualUnits: null, // Manual unit override (if set)
    };

    // ========================================================================
    // ROOM MIGRATION
    // ========================================================================
    // Transform flat room fields into nested room object
    // Old: { roomwidth: 4, roomlength: 5, roomheight: 2.7, roomunit: "metric", ... }
    // New: { room: { width: 4, length: 5, height: 2.7, unit: "metric", ... } }

    const migratedRoom = {
        width: userData.roomwidth || 0, // Room width dimension
        length: userData.roomlength || 0, // Room length dimension
        height: userData.roomheight || 0, // Room height dimension
        unit: userData.roomunit || 'imperial', // Measurement unit
        materials: userData.roommaterials || [], // Selected material types
        usage: userData.roomusage || '', // Room usage type description
        usagetarget: userData.roomusagetarget || '', // Target T60 reverb time
    };

    // ========================================================================
    // BUILD NEW USER DATA OBJECT
    // ========================================================================
    // Spread existing userData to preserve all other fields (firstname, email, etc.)
    // Add new multi-product structure
    // Set flag to trigger post-migration product data refresh

    const migratedUserData = {
        ...userData, // Preserve all existing fields

        // New multi-product structure
        products: [migratedProduct], // Array of products (starts with 1)
        activeProductId: productId, // UUID of currently active product
        room: migratedRoom, // Nested room object

        // Migration metadata
        _needsProductDataRefresh: true, // Flag for post-migration refresh
        _migrationVersion: 1, // Track which migrations have run
    };

    // ========================================================================
    // CLEANUP OLD FIELDS
    // ========================================================================
    // Remove old flat fields to avoid confusion and keep data structure clean
    // These fields are no longer used and have been migrated to new structure

    // Old product fields
    delete migratedUserData.product;
    delete migratedUserData.productsize;
    delete migratedUserData.productsizevariant;
    delete migratedUserData.productdata;
    delete migratedUserData.productsize_metric_sabins;

    // Old room fields
    delete migratedUserData.roomwidth;
    delete migratedUserData.roomlength;
    delete migratedUserData.roomheight;
    delete migratedUserData.roomunit;
    delete migratedUserData.roommaterials;
    delete migratedUserData.roomusage;
    delete migratedUserData.roomusagetarget;

    console.log('[Migration v1] Complete: Product migrated -', migratedProduct.product, migratedProduct.size);
    console.log('[Migration v1] Metric sabins value:', migratedProduct.metric_sabins);
    console.log('[Migration v1] Product data will be refreshed from current catalog');

    return migratedUserData;
}

/**
 * Post-migration hook - runs after store is initialized
 * Refreshes stale product catalog data, populates extended data, and runs batch calculation
 *
 * Why needed:
 * 1. The old saved product data may be outdated or incomplete
 * 2. The new structure needs current catalog data with proper size/variant info
 * 3. Extended data (lumen specs) needs to be looked up from productsExtended
 * 4. Batch calculation populates product.calculation (required units, T60, etc.)
 *    which is needed for lumen table calculations in the report
 *
 * @param {Object} userData - Migrated user data
 * @param {Object} store - Pinia store instance
 */
async function postMigrate(userData, store) {
    // Only run if product data refresh is needed
    if (!userData._needsProductDataRefresh) {
        return;
    }

    console.log('[Migration v1] Waiting for product catalog to load...');

    // ========================================================================
    // WAIT FOR PRODUCT CATALOG TO LOAD
    // ========================================================================
    // The product catalog is fetched asynchronously on app load
    // We need to wait for it before we can refresh product data

    const checkCatalogInterval = setInterval(async () => {
        // Check if catalog has loaded (store.products is populated)
        if (store.products && store.products.length > 0) {
            console.log('[Migration v1] Product catalog loaded, refreshing product data...');
            clearInterval(checkCatalogInterval);

            // ================================================================
            // REFRESH PRODUCT DATA FROM CATALOG
            // ================================================================
            // For each migrated product, replace stale catalog data with fresh data

            store.user.products.forEach((product) => {
                const productIndex = store.user.products.findIndex((p) => p.id === product.id);
                if (productIndex === -1) return;

                const productCode = product.product;

                // Find product in current catalog by code
                const catalogProduct = store.products.find((p) => p.id === productCode);

                if (catalogProduct) {
                    console.log('[Migration v1] Refreshing catalog data for:', productCode);

                    // Update product with fresh catalog data
                    // Preserves user selections (size, variant, etc.) but updates catalog metadata
                    store.user.products[productIndex] = {
                        ...product, // Keep user selections
                        data: catalogProduct, // Replace with fresh catalog data
                    };

                    console.log('[Migration v1] Product data refreshed successfully');

                    // ========================================================
                    // POPULATE EXTENDED DATA (Lumen Options)
                    // ========================================================
                    // Extended data contains lumen output specs for each product size/variant
                    // This is a lookup from productsExtended catalog

                    let extendedData = [];

                    if (store.productsExtended && store.productsExtended[productCode]) {
                        // If product has variant, match on both size and variant
                        if (product.size_variant) {
                            console.log(
                                '[Migration v1] Looking up extended data - Product:',
                                productCode,
                                'Size:',
                                product.size,
                                'Variant:',
                                product.size_variant,
                            );

                            // Get variant info to match the label
                            const variantData = catalogProduct.sizes?.variants?.all?.[product.size_variant];
                            if (variantData) {
                                extendedData = store.productsExtended[productCode].filter(
                                    (item) => item.size === product.size && item.variant === variantData.l,
                                );
                            }
                        } else {
                            // No variant, match on size only (with empty variant)
                            console.log('[Migration v1] Looking up extended data - Product:', productCode, 'Size:', product.size);

                            extendedData = store.productsExtended[productCode].filter((item) => item.size === product.size && item.variant === '');
                        }

                        if (extendedData.length > 0) {
                            console.log('[Migration v1] Found extended data:', extendedData.length, 'items');
                            store.user.products[productIndex].dataExtended = extendedData;
                        } else {
                            console.log('[Migration v1] No extended data found for this size/variant');
                        }
                    }
                } else {
                    // Product not found in catalog (may have been discontinued)
                    console.warn('[Migration v1] Product not found in current catalog:', productCode);
                }
            });

            // ================================================================
            // TRIGGER BATCH CALCULATION
            // ================================================================
            // Run batch calculation to populate product.calculation
            // This is needed for:
            // - Required units (unitsUsed)
            // - Resulting T60
            // - Lumen table calculations in the report
            //
            // Note: This will run even if user hasn't reached Report step yet,
            // ensuring migrated data is complete for report generation

            // Check if user has all required data for calculation
            const hasRequiredData =
                store.user.room &&
                store.user.room.width > 0 &&
                store.user.room.length > 0 &&
                store.user.room.height > 0 &&
                store.user.room.usagetarget &&
                store.user.ceiling_nrc &&
                store.user.floor_nrc &&
                store.user.walls_nrc;

            if (hasRequiredData) {
                console.log('[Migration v1] Running batch calculation to populate product calculations...');

                // Debug: Log product data being sent to batch calculation
                store.user.products.forEach((product, idx) => {
                    console.log(`[Migration v1] Product ${idx} - ID: ${product.id}, Sabins: ${product.metric_sabins}`);
                });

                try {
                    const result = await store.calculateProductsBatch();

                    if (result) {
                        console.log('[Migration v1] Batch calculation completed successfully');

                        // Debug: Check if calculation was actually saved to products
                        const firstProduct = store.getPrimaryProduct;
                        console.log('[Migration v1] Product calculation after batch:', firstProduct?.calculation);
                        console.log('[Migration v1] Units used:', firstProduct?.calculation?.unitsUsed);

                        if (firstProduct?.calculation?.unitsUsed) {
                            console.log('[Migration v1] Product calculations populated (units, T60, lumen data)');
                        } else {
                            console.warn('[Migration v1] Batch calculation completed but unitsUsed is missing');
                        }
                    } else {
                        console.warn('[Migration v1] Batch calculation returned no result');
                    }
                } catch (error) {
                    console.error('[Migration v1] Failed to run batch calculation:', error);
                    // Don't fail the migration, just log the error
                    // Calculations will run when user navigates to Product/Report step
                }
            } else {
                console.log('[Migration v1] Skipping batch calculation - user data incomplete');
                console.log('[Migration v1] Calculation will run when user navigates to Product/Report step');
            }

            // ================================================================
            // CLEANUP MIGRATION FLAGS
            // ================================================================
            // Remove temporary migration flags so they don't persist

            delete store.user._needsProductDataRefresh;

            console.log('[Migration v1] Post-migration refresh complete');
        }
    }, 100); // Check every 100ms until catalog loads
}

// ============================================================================
// EXPORT MIGRATION OBJECT
// ============================================================================
// This object is imported by the migration manager and executed in sequence

export const v1_multi_product = {
    version: 1,
    name: 'multi-product',
    description: 'Migrates from flat product structure to multi-product array structure',
    needsMigration,
    migrate,
    postMigrate,
};
